import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Term} from '../../../../../enote/enote_models/term';
import {ModalDirective} from 'ngx-bootstrap/modal';
import {AlertService} from '../../../../../_alert';
import {GlobalErrorHandlerService} from '../../../../../services/global-error.service';
import {MedicationSummaryService} from './medication-summary.service';
import {LoaderService} from '../../../../../services/loader.service';
import {Control} from '../../../../../enote/enote_models/control';
import {Observable} from 'rxjs';
import {TermControl, TermControlValue} from '../../../../../enote/enote_models/term_control';
import {mergeMap} from 'rxjs/operators';
import {ProgramService} from '../../../../../program/program.service';
import {UserService} from '../../../../../services/user.service';
import {TypeaheadMatch} from 'ngx-bootstrap/typeahead';
import {EnrolmentsService} from '../../../../../program/enrolments.service';

@Component({
  selector: 'app-medication-summary',
  templateUrl: './medication-summary.component.html',
  styleUrls: ['./medication-summary.component.scss']
})
export class MedicationSummaryComponent implements OnInit {
  @ViewChild('updateTermStatus') updateTermStatus: ModalDirective;
  @ViewChild('addProblem') addProblem: ModalDirective;
  @ViewChild('diagnosisInput') diagnosisInputRef: ElementRef<HTMLInputElement>;
  @ViewChild('termInput') termInputRef: ElementRef<HTMLInputElement>;
  public showUpdateTermModel: boolean;
  public selectedTerm: Term;
  public changeStatusWarning: boolean;
  public updateMedication: boolean;
  public smallStatusLoader: boolean;
  @Input() patient_id: any;
  public custom_controls: Control[] = [];
  public default_controls: Control[] = [];
  public showAddProblemModel: boolean;
  public typeaheadLoading: boolean;
  public typeaheadNoResults: boolean;
  public diagnosis_token = '';
  public term_token = '';
  public data_source_diagnosis: Observable<any>;
  public data_term_source: Observable<any>;
  public baseTerm: Term = new Term('', '')
  public term_for: string;
  public editPatientSummary: boolean;
  @Output() getPatientSummaryEmitter: EventEmitter<any> = new EventEmitter();
  public formDisabled = false;
  public textMapper: Object = {'Free Text': 'Enter additional comments', 'Onset': 'Enter full date or month-year', 'medications': 'Medication', 'problems': 'Problem', 'allergies': 'Allergy'};
  private prefillName: string = null;
  private prefillOnset: string = null;
  private prefillCode: string = null;
  private prefillControls: { [key: string]: string } = null;
  public autoMatchLoading = false;   // background term auto-selection in progress


  constructor(public alertService: AlertService, public loaderService: LoaderService,
              public userService: UserService, public enrolmentsService: EnrolmentsService,
              public medicationSummaryService: MedicationSummaryService, public programService: ProgramService,
              public globalErrorHandlerService: GlobalErrorHandlerService) { }

  ngOnInit(): void {
    this.data_source_diagnosis = Observable.create((observer: any) => {
      observer.next(this.diagnosis_token);
    }).pipe(mergeMap((token: string) => this.programService.searchDiagnosis(token)));

    this.data_term_source = Observable.create((observer: any) => {
      observer.next(this.term_token);
    }).pipe(mergeMap((token: string) => this.medicationSummaryService.searchTermsAllergiesMedication(token, this.term_for)));
  }

  updateTermsStatus(term: Term) {
    this.smallStatusLoader = true;
    this.medicationSummaryService.changeTermStatus({id: term.ps_id, status: term.status}).then( resp => {
      this.loaderService.show();
      this.updateProgramBanner(resp);
      this.getPatientSummaryCallback();
      this.smallStatusLoader = false;
      this.closeUpdateTermModel();
      this.alertService.success('Status has been saved successfully', this.alertService.options);
    }).catch( resp => {
          this.smallStatusLoader = false;
          this.changeStatusWarning = false;
          this.globalErrorHandlerService.error = resp.error.error;
        }
    )
  }

  closeUpdateTermModel() {
    this.updateTermStatus.hide();
    this.showUpdateTermModel = false;
    this.changeStatusWarning = false;
    this.updateMedication = false;
  }

  // Open the Add modal with the search token prefilled (e.g. from a received C-CDA item).
  // When name or code matches a library term exactly the term is auto-selected;
  // otherwise staff picks from the typeahead as usual. `controls` maps a control
  // name fragment to the desired value, e.g. { route: 'Oral', frequency: 'Every 12 h' }.
  openWithPrefill(type: string, name: string, onset: string = null, code: string = null,
                  controls: { [key: string]: string } = null): void {
    this.prefillName = name || '';
    this.prefillOnset = onset;
    this.prefillCode = code || null;
    this.prefillControls = controls;
    this.getControls(type);
  }

  getControls(type: string) {
    this.loaderService.show();
    this.term_for = type;
    this.diagnosis_token = '';
    this.typeaheadNoResults = false;
    this.typeaheadLoading = false;
    this.medicationSummaryService.getCustomAndDefaultControls(type).then(resp => {
      this.baseTerm = new Term('', '');
      if (resp.custom_controls.length > 1) {
        this.baseTerm.term_controls = resp.custom_controls.map(control => {
          let term_control = new TermControl();
          term_control.control_id = control.id;
          term_control.term_control_values = control.values.map(val => {
            let vale = new TermControlValue().load_from_json_without_id(val);
            return vale
          })
          term_control.control = new Control('', '').load_from_json(control);
          return term_control;
        });
      }
      if (resp.default_controls.length > 1) {
        this.default_controls = resp.default_controls.map(control => new Control('', '').load_from_json(control));
      }
      this.openAddProblemModel();
      this.applyPrefill(type);
      this.loaderService.hide();

    }).catch( resp => {
      this.loaderService.hide();
      this.globalErrorHandlerService.error = resp.error.error;
    })
  }

  private applyPrefill(type: string): void {
    if (this.prefillName === null && this.prefillCode === null) { return; }
    if (this.prefillOnset && this.baseTerm.term_controls) {
      const onsetControl = this.baseTerm.term_controls.find(tc => tc.control && tc.control.name === 'Onset');
      if (onsetControl && onsetControl.term_control_values && onsetControl.term_control_values.length > 0) {
        onsetControl.term_control_values[0].val_name = this.prefillOnset;
        onsetControl.term_control_values[0].recent_value = true;
      }
    }
    this.applyControlPrefills(this.prefillControls);
    if (type === 'problems') {
      this.autoMatchDiagnosis(this.prefillName, this.prefillCode);
    } else {
      this.autoMatchTerm(this.prefillName, type);
    }
    this.prefillName = null;
    this.prefillOnset = null;
    this.prefillCode = null;
    this.prefillControls = null;
  }

  // Generic prefill for the term's custom controls (Form, Route, Med Frequency, …).
  // Dropdowns pre-select the option whose value matches; text inputs take the raw
  // value. When nothing matches, the control is simply left for staff to set.
  private applyControlPrefills(map: { [key: string]: string }): void {
    if (!map || !this.baseTerm.term_controls) { return; }
    const norm = (s: any) => String(s || '').toLowerCase().replace(/-/g, ' ').replace(/\s+/g, ' ').trim();

    Object.keys(map).forEach(key => {
      const wanted = String(map[key] || '').trim();
      if (!wanted) { return; }
      const tc = this.baseTerm.term_controls.find(t =>
        t.control && norm(t.control.name).includes(norm(key)));
      if (!tc || !tc.term_control_values || tc.term_control_values.length === 0) { return; }

      if (tc.control.native_type === 'input_text') {
        tc.term_control_values[0].val_name = wanted;
        tc.term_control_values[0].recent_value = true;
        return;
      }

      const candidates = this.controlValueCandidates(key, wanted);
      let match = null;
      for (const c of candidates) {   // exact first, across all candidates in priority order
        match = tc.term_control_values.find(v => norm(v.val_name) === norm(c));
        if (match) { break; }
      }
      if (!match) {
        for (const c of candidates) { // then loose containment either way
          match = tc.term_control_values.find(v =>
            norm(v.val_name).includes(norm(c)) || norm(c).includes(norm(v.val_name)));
          if (match) { break; }
        }
      }
      if (match) {
        tc.term_control_values.forEach(v => v.recent_value = false);
        match.recent_value = true;
      }
    });
  }

  // Translate C-CDA vocabulary into this library's option wording, in priority
  // order (e.g. "Every 12 h" → "twice a day", "Oral" → "orally").
  private controlValueCandidates(key: string, wanted: string): string[] {
    const w = wanted.toLowerCase();
    if (key === 'frequency') {
      const hours = w.match(/every\s*(\d+(?:\.\d+)?)\s*h/);
      const byHours: { [h: number]: string[] } = {
        4:  ['every four hours'],
        6:  ['every six hours', 'four times a day'],
        8:  ['three times a day'],
        12: ['twice a day'],
        24: ['once a day'],
        48: ['every other day'],
      };
      if (hours && byHours[+hours[1]]) { return byHours[+hours[1]].concat([w]); }
      const days = w.match(/every\s*(\d+)\s*d/);
      if (days) {
        if (+days[1] === 1) { return ['once a day', w]; }
        if (+days[1] === 2) { return ['every other day', w]; }
      }
      return [w];
    }
    if (key === 'route') {
      if (w.includes('oral') || w === 'po' || w.includes('mouth')) { return ['orally', w]; }
      if (w.includes('intramuscul') || w === 'im') { return ['intramuscularly', 'intramuscular', w]; }
      if (w.includes('intraven') || w === 'iv') { return ['intravenous', w]; }
      if (w.includes('subcut') || w.includes('subq')) { return ['subcutaneous', w]; }
      if (w.includes('rect')) { return ['rectally', w]; }
      return [w];
    }
    return [w];
  }

  // Auto-select for medications/allergies: name-only search; select only when
  // exactly one exact name match exists, otherwise fall back to the dropdown.
  private autoMatchTerm(name: string, type: string): void {
    const fallback = () => {
      this.autoMatchLoading = false;
      this.term_token = name || '';
      if (this.term_token) { this.triggerTypeaheadSearch(type); }
    };
    if (!name) { fallback(); return; }
    this.autoMatchLoading = true;
    this.medicationSummaryService.searchTermsAllergiesMedication(name, type)
      .then(results => {
        if (!this.showAddProblemModel) { this.autoMatchLoading = false; return; }
        const target = name.toLowerCase().trim();
        const exacts = (results || []).filter(r =>
          String(r.name || '').toLowerCase().trim() === target ||
          String(r.sname || '').toLowerCase().trim() === target);
        if (exacts.length === 1) {
          this.autoMatchLoading = false;
          this.term_token = exacts[0].sname || exacts[0].name;
          this.typeaheadTermOnSelect({ item: exacts[0] } as TypeaheadMatch);
        } else {
          fallback();
        }
      })
      .catch(() => fallback());
  }

  // ICD-10 codes are displayed dotted (E11.9) but stored/transmitted dotless
  // (E119) — the diagnosis library uses the stored form.
  private dotless(code: string): string {
    return (code || '').replace(/\./g, '').toLowerCase().trim();
  }

  // Try to resolve the prefilled problem to exactly one library term so staff
  // doesn't have to pick from the dropdown: exact ICD-10 code match first,
  // then exact name match, else fall back to the prefilled typeahead search.
  private autoMatchDiagnosis(name: string, code: string): void {
    const fallbackToTypeahead = () => {
      this.autoMatchLoading = false;
      this.diagnosis_token = name || '';
      if (this.diagnosis_token) { this.triggerTypeaheadSearch('problems'); }
    };
    const tryExactName = () => {
      if (!name) { fallbackToTypeahead(); return; }
      this.programService.searchDiagnosis(name)
        .then(results => {
          if (!this.showAddProblemModel) { this.autoMatchLoading = false; return; }
          const exact = (results || []).find(r =>
            String(r.name || '').toLowerCase().trim() === name.toLowerCase().trim());
          exact ? this.autoSelectDiagnosis(exact) : fallbackToTypeahead();
        })
        .catch(() => fallbackToTypeahead());
    };

    this.autoMatchLoading = true;
    if (!this.dotless(code)) { tryExactName(); return; }
    this.programService.searchDiagnosis(this.dotless(code))
      .then(results => {
        if (!this.showAddProblemModel) { this.autoMatchLoading = false; return; }
        const exact = (results || []).find(r => this.dotless(r.code) === this.dotless(code));
        exact ? this.autoSelectDiagnosis(exact) : tryExactName();
      })
      .catch(() => tryExactName());
  }

  // Same effect as the user clicking the term in the typeahead dropdown.
  private autoSelectDiagnosis(item: any): void {
    this.autoMatchLoading = false;
    this.diagnosis_token = item.d_name || item.name;
    this.typeaheadDiagnosisOnSelect({ item } as TypeaheadMatch);
  }

  // ngx-bootstrap typeahead only reacts to real input events, not programmatic
  // ngModel changes — dispatch one after the modal renders so the prefilled
  // token opens the suggestion dropdown without the user having to retype.
  private triggerTypeaheadSearch(type: string): void {
    setTimeout(() => {
      const ref = type === 'problems' ? this.diagnosisInputRef : this.termInputRef;
      if (ref && ref.nativeElement) {
        ref.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }, 600);
  }

  changeTypeaheadLoading(e: boolean) {
    this.typeaheadLoading = e;
  }
  changeTypeaheadNoResults(e: boolean) {
    if (this.term_for !== 'problems') {
      this.noResultsForMedicationAndAllergies();
    } else {
      this.typeaheadNoResults = e;
    }
  }

  noResultsForMedicationAndAllergies() {
    this.baseTerm.name = this.term_token;
    this.setTermAndComponentType();
  }


  setValue(control_value_id: any, id: any) {
    let selected_term: TermControl = this.selectTerm(id);
    if (selected_term.term_control_values && control_value_id) {
      selected_term.term_control_values.filter(a => a.control_value_id.toString() === control_value_id.toString())[0].recent_value = true
    }
  }

  addTerm() {
    let for_library = this.term_for === 'allergies' ? 'general' : this.term_for
    this.loaderService.show();
    this.medicationSummaryService.addPatientProblem(this.baseTerm, for_library, this.patient_id).then( resp => {
      if (resp.summary_id) {
        this.saveSummaryCallback();
      } else {
        this.createPatientSummaryObject(resp)
      }
    }).catch(resp => {
      this.loaderService.hide();
      this.globalErrorHandlerService.error = resp.error.error;
    })
  }

  addPatientSummary(summary: any) {
    this.medicationSummaryService.addPatientSummary(summary).then(res => {
      this.updateProgramBanner(res);
      this.saveSummaryCallback();
    }).catch(rep => {
      this.loaderService.hide();
      this.globalErrorHandlerService.error = rep.error.error;
    })
  }

  saveSummaryCallback() {
    this.closeAddProblemModel();
    this.getPatientSummaryCallback();
    this.showAlertMessage();
  }

  showAlertMessage() {
    this.alertService.success(this.userService.capitalize(this.term_for) + ' has been saved successfully',
        this.alertService.options);
  }

  updateTerm() {
    this.loaderService.show();
    this.medicationSummaryService.update(this.baseTerm).then ( resp => {
      console.log(resp)
      this.updateProgramBanner(resp);
      this.saveSummaryCallback();
    }).catch( resp => {
      this.loaderService.hide();
      this.globalErrorHandlerService.error = resp.error.error;
    })
  }

  selectTerm(id: any) {
    let term: TermControl = this.baseTerm.term_controls.filter(a => a.control_id === id)[0];
    if (term.control_id) {
      term.term_control_values.map(a => a.recent_value = false);
    }
    return term;
  }

  editControl(selectedTerm: Term) {
    this.closeUpdateTermModel();
    this.baseTerm = new Term('', '').load_from_json2(selectedTerm);
    this.term_for = this.baseTerm.data_type;
    this.editPatientSummary = true
    setTimeout(() => {
      this.openAddProblemModel();
    }, 600);
  }

  openAddProblemModel() {
    this.showAddProblemModel = true;
    this.addProblem.show();
  }

  typeaheadTermOnSelect(prob: TypeaheadMatch) {
    this.baseTerm.d_name = prob.item.sname;
    this.setTermFields(prob.item);
  }

  typeaheadDiagnosisOnSelect(prob) {
    this.baseTerm.d_name = prob.item.d_name;
    this.baseTerm.is_chronic = prob.item.is_chronic ? true : false;
    this.setTermFields(prob.item);
  }

  setTermFields(term) {
    this.baseTerm.library_datum_id = term.library_datum_id;
    this.baseTerm.name = term.name;
    this.setTermAndComponentType();
  }

  setTermAndComponentType() {
    this.baseTerm.term_type = 'term';
    this.baseTerm.component_type = 'term';
    this.formDisabled = true;
  }

  closeAddProblemModel() {
    this.addProblem.hide();
    this.editPatientSummary = false;
    this.showAddProblemModel = false;
    this.diagnosis_token = '';
    this.term_token = '';
    this.formDisabled = false;
    this.autoMatchLoading = false;
  }

  openUpdateTermStatusModel(term: any, type?: string) {
    this.selectedTerm = term;
    this.updateTermStatus.show();
    this.showUpdateTermModel = true;
    if (type === 'medications') {
      this.updateMedication = true;
    }
  }

  checkString(event: KeyboardEvent, token: string) {
    if (event.key === 'Backspace') {
      this.clearBaseTermFields();
    }
  }

  showLabel(name: string): string {
    return name === 'Free Text' ? 'Additional information' : name;
  }

  getPatientSummaryCallback () {
    this.getPatientSummaryEmitter.emit(true);
  }

  clearBaseTermFields() {
    this.formDisabled = false;
    this.baseTerm.d_name = undefined;
    this.baseTerm.library_datum_id = undefined;
    this.baseTerm.name = undefined;
    this.baseTerm.term_type = undefined;
    this.baseTerm.component_type = undefined;
  }

  private updateProgramBanner(res: any) {
    if (res.data_type === 'problems' || res.data_type === 'allergies') {
      if (res['chronic_diagnosis_name_code'] && res.data_type === 'problems') {
        this.enrolmentsService.reloadSummary.next(
            {chronic_diagnosis_name_code: res['chronic_diagnosis_name_code'], patient_id: this.patient_id}
        )
      }
      if (res['allergies_name'] && res.data_type === 'allergies') {
        this.enrolmentsService.reloadSummary.next( {allergies: res['allergies_name'], patient_id: this.patient_id})
      }
    }
  }

  private createPatientSummaryObject(resp: any) {
    let summary = {data_type: this.term_for, base_term_id: resp.id,  patient_id: this.patient_id,
      status: 'current', library_datum_id: resp.library_datum_id,
      practice_id: this.userService.current_user.profileable['practice_id']}
    this.addPatientSummary(summary);
  }

  disabledAddForm(): boolean {
    return this.baseTerm.name.trim().length === 0
  }
}
